import React, { useMemo } from "react";

import Select from "antd/es/select";
import { ColumnType } from "antd/es/table";
import { defineMessages, IntlShape } from "react-intl";

import User, { UserRef } from "@mapmycustomers/shared/types/User";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import IntegrationUser from "@app/types/integrations/IntegrationUser";

import MappingInformation from "../MappingInformation";

import { IntegrationUserWithSyncUnmappedUsers } from "./Step1";
import styles from "./Steps.module.scss";

export const UNASSIGNED_USER_ID = -1;

const messages = defineMessages({
  serviceName: {
    id: "integrations.setupStep.column.email",
    defaultMessage: "{serviceName} User",
    description: "Title of a email field",
  },
  status: {
    id: "integrations.setupStep.column.status",
    defaultMessage: "Status",
    description: "Title of a status field",
  },
  unassigned: {
    id: "integrations.setupStep.column.unassigned",
    defaultMessage: "Unassigned",
    description: "Unassigned user row",
  },
  unassignedTooltip: {
    id: "integrations.setupStep.column.unassignedTooltip",
    defaultMessage: "Select a MMC user to ensure unassigned records in your CRM are synced ",
    description: "Unassigned tooltip text user row",
  },
  userId: {
    id: "integrations.setupStep.column.userId",
    defaultMessage: "Map My Customers User",
    description: "Title of a userId field",
  },
});

const useUserTableColumnConfig = (
  intl: IntlShape,
  onChangeMapping: (mapping: { integrationUser: IntegrationUser; mmcUserId?: User["id"] }) => void,
  serviceName: string,
  users: UserRef[],
  onChangeUnassignedRecordsOwner: (mmcUserId?: User["id"]) => void,
  unassignedRecordsOwner?: User["id"]
): ColumnType<IntegrationUserWithSyncUnmappedUsers>[] =>
  useMemo(
    () => [
      {
        dataIndex: "email",
        key: "email",
        render: (email, integrationUser: IntegrationUserWithSyncUnmappedUsers) => (
          <>
            {integrationUser.id === UNASSIGNED_USER_ID ? (
              <TextWithInfo info={intl.formatMessage(messages.unassignedTooltip)} placement="top">
                {intl.formatMessage(messages.unassigned)}
              </TextWithInfo>
            ) : (
              email
            )}
          </>
        ),
        title: intl.formatMessage(messages.serviceName, { serviceName }),
      },
      {
        className: styles.limitedColumn,
        dataIndex: "userId",
        key: "userId",
        render: (
          mmcUserId: IntegrationUser["userId"],
          integrationUser: IntegrationUserWithSyncUnmappedUsers
        ) => {
          let value = mmcUserId;
          let onChange = (selectedUserId?: User["id"]) =>
            onChangeMapping({ integrationUser, mmcUserId: selectedUserId });

          if (integrationUser.id === UNASSIGNED_USER_ID) {
            value = unassignedRecordsOwner ?? null;
            onChange = onChangeUnassignedRecordsOwner;
          }

          const options = [
            {
              label: intl.formatMessage({
                id: "integrations.setupStep.column.userId.none",
                defaultMessage: "None",
                description: "None option in user mapping table in integrations",
              }),
              value: 0,
            },
            ...users.map((user) => ({
              label: user.username,
              value: user.id,
            })),
          ];

          return (
            <Select<User["id"]>
              className={styles.userSelect}
              dropdownMatchSelectWidth={false}
              filterOption={(input, option) =>
                (option?.label as string).toLowerCase().includes(input.toLowerCase())
              }
              onChange={(selectedUserId: 0 | User["id"]) => onChange(selectedUserId || undefined)}
              options={options}
              showSearch
              value={value ?? 0}
            />
          );
        },
        title: intl.formatMessage(messages.userId),
      },
      {
        dataIndex: "userId",
        key: "status",
        render: (mmcUserId, integrationUser: IntegrationUserWithSyncUnmappedUsers) => {
          const mapped =
            !!mmcUserId || (integrationUser.id === UNASSIGNED_USER_ID && !!unassignedRecordsOwner);
          return <MappingInformation mapped={mapped} />;
        },
        title: intl.formatMessage(messages.status),
        width: 200,
      },
    ],
    [
      onChangeMapping,
      serviceName,
      users,
      intl,
      unassignedRecordsOwner,
      onChangeUnassignedRecordsOwner,
    ]
  );

export default useUserTableColumnConfig;
